"use client";

import { CircleCheck, CircleX, LoaderCircle, X } from "lucide-react";
import { useEffect } from "react";

interface StatusToastProps {
  isVisible: boolean;
  onClose: () => void;
  isSuccess: boolean;
  isLoading?: boolean;
  title: string;
  message: string;
  duration?: number;
}

export default function StatusToast({
  isVisible,
  onClose,
  isSuccess,
  isLoading = false,
  title,
  message,
  duration = 5000,
}: StatusToastProps) {
  // Auto dismiss after duration (skip while loading)
  useEffect(() => {
    if (isVisible && !isLoading && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isLoading, duration, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-6 right-6 z-100 animate-slide-in-right">
      <div className="relative overflow-visible flex items-center gap-2.5 w-89 p-[17px_14px] bg-[#0B0E17] border border-[rgba(255,255,255,0.1)] rounded-xl shadow-[0px_10px_25px_rgba(0,0,0,0.3)]">
        {!isLoading && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close toast"
            className="absolute -top-2 -right-2 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-[#0B0E17] border border-[rgba(255,255,255,0.15)] text-[#AAB3D0] hover:text-white hover:bg-[#121725] transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Status Icon */}
        <div className="shrink-0">
          {isLoading ? (
            // Loading spinner
            <div className="w-5 h-5 flex items-center justify-center rounded-full">
              <LoaderCircle className="animate-spin text-[#AAB3D0]" />
            </div>
          ) : isSuccess ? (
            // Checkmark icon for success
            <div className="w-5 h-5 flex items-center justify-center bg-[#0E966F] rounded-full">
              <CircleCheck className="text-white" />
            </div>
          ) : (
            // X icon for error
            <div className="w-5 h-5 flex items-center justify-center bg-[#FF5C16] rounded-full">
              <CircleX className="text-black" />
            </div>
          )}
        </div>

        {/* Text Content */}
        <div className="flex flex-col gap-0.5">
          <span className="font-vietnam font-medium text-[13px] leading-5 tracking-[-0.076px] text-white">
            {title}
          </span>
          <span className="font-vietnam font-normal text-[13px] leading-4.5 tracking-[-0.076px] text-[#AAB3D0]">
            {message}
          </span>
        </div>
      </div>
    </div>
  );
}
